import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Car, Users, Plus, Edit, Trash2, LogOut, LayoutDashboard,
  ChevronDown, ChevronUp, Eye, Mail, Phone, Calendar, Tag,
  X, Check, Loader2, Menu, Download, FileSpreadsheet, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vehicles');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    image_url: '',
    description: '',
    features: '',
    is_featured: false
  });
  const [sheetsStatus, setSheetsStatus] = useState({ configured: false, connected: false, sheet_url: null });

  const token = localStorage.getItem('admin_token');
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [vehiclesRes, leadsRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/leads`, axiosConfig)
      ]);
      setVehicles(vehiclesRes.data);
      setLeads(leadsRes.data);
      
      // Fetch Google Sheets status
      try {
        const sheetsRes = await axios.get(`${API}/admin/sheets/status`, axiosConfig);
        setSheetsStatus(sheetsRes.data);
      } catch (e) {
        // Sheets status is optional
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleVehicleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVehicleForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddVehicle = () => {
    setEditingVehicle(null);
    setVehicleForm({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      price: '',
      mileage: '',
      image_url: '',
      description: '',
      features: '',
      is_featured: false
    });
    setShowVehicleModal(true);
  };

  const openEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price.toString(),
      mileage: vehicle.mileage?.toString() || '',
      image_url: vehicle.image_url,
      description: vehicle.description || '',
      features: vehicle.features?.join(', ') || '',
      is_featured: vehicle.is_featured
    });
    setShowVehicleModal(true);
  };

  const handleSaveVehicle = async () => {
    try {
      const payload = {
        ...vehicleForm,
        year: parseInt(vehicleForm.year),
        price: parseFloat(vehicleForm.price),
        mileage: vehicleForm.mileage ? parseInt(vehicleForm.mileage) : null,
        features: vehicleForm.features ? vehicleForm.features.split(',').map(f => f.trim()) : []
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, payload, axiosConfig);
        toast.success('Vehicle updated successfully');
      } else {
        await axios.post(`${API}/vehicles`, payload, axiosConfig);
        toast.success('Vehicle added successfully');
      }

      setShowVehicleModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, axiosConfig);
      toast.success('Vehicle deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status?status=${status}`, {}, axiosConfig);
      toast.success('Lead status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const seedVehicles = async () => {
    try {
      await axios.post(`${API}/seed/vehicles`);
      toast.success('Sample vehicles added');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed vehicles');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.new;
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#E5E0D6] transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#E5E0D6]">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#2C4A3B] rounded-full flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-[#2C4A3B]">YesApproved</span>
            </a>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <button
              onClick={() => { setActiveTab('vehicles'); setSidebarOpen(false); }}
              className={`w-full sidebar-link ${activeTab === 'vehicles' ? 'sidebar-link-active' : ''}`}
              data-testid="nav-vehicles"
            >
              <Car className="w-5 h-5" />
              <span>Vehicles</span>
            </button>
            <button
              onClick={() => { setActiveTab('leads'); setSidebarOpen(false); }}
              className={`w-full sidebar-link ${activeTab === 'leads' ? 'sidebar-link-active' : ''}`}
              data-testid="nav-leads"
            >
              <Users className="w-5 h-5" />
              <span>Leads</span>
              {leads.filter(l => l.status === 'new').length > 0 && (
                <span className="ml-auto bg-[#C07756] text-white text-xs px-2 py-0.5 rounded-full">
                  {leads.filter(l => l.status === 'new').length}
                </span>
              )}
            </button>
          </nav>

          <div className="p-4 border-t border-[#E5E0D6]">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 rounded-full bg-[#2C4A3B] flex items-center justify-center text-white font-bold">
                {adminUser.username?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#2C4A3B] truncate">{adminUser.username}</p>
                <p className="text-xs text-[#5A5A5A] truncate">{adminUser.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full sidebar-link text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-[#E5E0D6] px-4 lg:px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-[#F0EBE0] rounded-lg"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-6 h-6 text-[#2C4A3B]" />
              </button>
              <h1 className="font-heading text-2xl font-bold text-[#2C4A3B]">
                {activeTab === 'vehicles' ? 'Vehicle Inventory' : 'Lead Management'}
              </h1>
            </div>
            
            {activeTab === 'vehicles' && (
              <div className="flex gap-2">
                <Button
                  onClick={seedVehicles}
                  variant="outline"
                  className="btn-secondary hidden sm:flex"
                  data-testid="seed-vehicles-btn"
                >
                  Seed Sample Data
                </Button>
                <Button
                  onClick={openAddVehicle}
                  className="btn-primary"
                  data-testid="add-vehicle-btn"
                >
                  <Plus className="w-5 h-5 mr-2" /> Add Vehicle
                </Button>
              </div>
            )}
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#2C4A3B]" />
            </div>
          ) : activeTab === 'vehicles' ? (
            /* Vehicles Tab */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
              {vehicles.length === 0 ? (
                <div className="col-span-full text-center py-20">
                  <Car className="w-16 h-16 text-[#E5E0D6] mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#2C4A3B] mb-2">No Vehicles Yet</h3>
                  <p className="text-[#5A5A5A] mb-6">Add your first vehicle or seed sample data</p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={seedVehicles} variant="outline" className="btn-secondary">
                      Seed Sample Data
                    </Button>
                    <Button onClick={openAddVehicle} className="btn-primary">
                      <Plus className="w-5 h-5 mr-2" /> Add Vehicle
                    </Button>
                  </div>
                </div>
              ) : (
                vehicles.map((vehicle) => (
                  <div 
                    key={vehicle.id} 
                    className="bg-white rounded-2xl border border-[#E5E0D6] overflow-hidden card-hover"
                    data-testid={`vehicle-item-${vehicle.id}`}
                  >
                    <div className="relative h-48">
                      <img
                        src={vehicle.image_url}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                      />
                      {vehicle.is_featured && (
                        <span className="absolute top-3 left-3 bg-[#C07756] text-white px-3 py-1 rounded-full text-sm font-medium">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-lg font-semibold text-[#2C4A3B] mb-1">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                      {vehicle.mileage && (
                        <p className="text-sm text-[#5A5A5A] mb-2">{vehicle.mileage.toLocaleString()} km</p>
                      )}
                      <p className="text-xl font-bold text-[#C07756] mb-4">
                        ${vehicle.price.toLocaleString()}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openEditVehicle(vehicle)}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          data-testid={`edit-vehicle-${vehicle.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteVehicle(vehicle.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-vehicle-${vehicle.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Leads Tab */
            <div className="space-y-4" data-testid="leads-list">
              {/* Export Controls */}
              <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white rounded-xl border border-[#E5E0D6]">
                <Button
                  onClick={() => {
                    window.open(`${API}/leads/export/csv`, '_blank');
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4" /> Export CSV
                </Button>
                
                {sheetsStatus.connected ? (
                  <a
                    href={sheetsStatus.sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    data-testid="google-sheets-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> View Live Google Sheet
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <a
                    href="https://docs.google.com/spreadsheets/create"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    data-testid="google-sheets-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Open Google Sheets
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                
                <div className="flex items-center gap-2 text-sm">
                  {sheetsStatus.connected ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <Check className="w-4 h-4" /> Auto-sync to Google Sheets enabled
                    </span>
                  ) : (
                    <span className="text-[#5A5A5A]">
                      Export CSV then import to Google Sheets: File → Import → Upload
                    </span>
                  )}
                </div>
              </div>

              {leads.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E0D6]">
                  <Users className="w-16 h-16 text-[#E5E0D6] mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#2C4A3B] mb-2">No Leads Yet</h3>
                  <p className="text-[#5A5A5A]">Leads will appear here when customers submit applications</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[#5A5A5A]">
                      Total: <strong className="text-[#2C4A3B]">{leads.length}</strong> leads
                    </p>
                  </div>
                  {leads.map((lead) => (
                  <div 
                    key={lead.id}
                    className="bg-white rounded-2xl border border-[#E5E0D6] p-6"
                    data-testid={`lead-item-${lead.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg font-semibold text-[#2C4A3B]">
                            {lead.full_name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(lead.status)}`}>
                            {lead.status}
                          </span>
                          {lead.is_first_nations && (
                            <span className="bg-[#2C4A3B]/10 text-[#2C4A3B] px-3 py-1 rounded-full text-xs font-medium">
                              First Nations
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-[#5A5A5A]">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${lead.email}`} className="hover:text-[#C07756]">{lead.email}</a>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${lead.phone}`} className="hover:text-[#C07756]">{lead.phone}</a>
                          </div>
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4" />
                            <span>Credit: {lead.credit_standing}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(lead.created_at)}</span>
                          </div>
                        </div>
                        
                        {lead.date_of_birth && (
                          <div className="mt-2 text-sm text-[#5A5A5A]">
                            <span className="font-medium">DOB:</span> {lead.date_of_birth}
                          </div>
                        )}

                        {lead.message && (
                          <p className="mt-3 text-sm text-[#5A5A5A] bg-[#F0EBE0] p-3 rounded-lg">
                            "{lead.message}"
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Select
                          defaultValue={lead.status}
                          onValueChange={(value) => handleUpdateLeadStatus(lead.id, value)}
                        >
                          <SelectTrigger className="w-36" data-testid={`status-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Vehicle Modal */}
      <Dialog open={showVehicleModal} onOpenChange={setShowVehicleModal}>
        <DialogContent className="sm:max-w-lg" data-testid="vehicle-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#2C4A3B]">
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="make" className="text-[#2C4A3B]">Make *</Label>
                <Input
                  id="make"
                  name="make"
                  value={vehicleForm.make}
                  onChange={handleVehicleInputChange}
                  placeholder="Toyota"
                  className="input-custom mt-1"
                  required
                  data-testid="modal-input-make"
                />
              </div>
              <div>
                <Label htmlFor="model" className="text-[#2C4A3B]">Model *</Label>
                <Input
                  id="model"
                  name="model"
                  value={vehicleForm.model}
                  onChange={handleVehicleInputChange}
                  placeholder="RAV4"
                  className="input-custom mt-1"
                  required
                  data-testid="modal-input-model"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year" className="text-[#2C4A3B]">Year *</Label>
                <Input
                  id="year"
                  name="year"
                  type="number"
                  value={vehicleForm.year}
                  onChange={handleVehicleInputChange}
                  className="input-custom mt-1"
                  required
                  data-testid="modal-input-year"
                />
              </div>
              <div>
                <Label htmlFor="price" className="text-[#2C4A3B]">Price ($) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  value={vehicleForm.price}
                  onChange={handleVehicleInputChange}
                  placeholder="29999"
                  className="input-custom mt-1"
                  required
                  data-testid="modal-input-price"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="mileage" className="text-[#2C4A3B]">Mileage (km)</Label>
              <Input
                id="mileage"
                name="mileage"
                type="number"
                value={vehicleForm.mileage}
                onChange={handleVehicleInputChange}
                placeholder="25000"
                className="input-custom mt-1"
                data-testid="modal-input-mileage"
              />
            </div>

            <div>
              <Label htmlFor="image_url" className="text-[#2C4A3B]">Image URL *</Label>
              <Input
                id="image_url"
                name="image_url"
                value={vehicleForm.image_url}
                onChange={handleVehicleInputChange}
                placeholder="https://..."
                className="input-custom mt-1"
                required
                data-testid="modal-input-image"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-[#2C4A3B]">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={vehicleForm.description}
                onChange={handleVehicleInputChange}
                placeholder="Brief description of the vehicle..."
                className="input-custom mt-1"
                data-testid="modal-input-description"
              />
            </div>

            <div>
              <Label htmlFor="features" className="text-[#2C4A3B]">Features (comma-separated)</Label>
              <Input
                id="features"
                name="features"
                value={vehicleForm.features}
                onChange={handleVehicleInputChange}
                placeholder="AWD, Heated Seats, Backup Camera"
                className="input-custom mt-1"
                data-testid="modal-input-features"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Checkbox
                id="is_featured"
                checked={vehicleForm.is_featured}
                onCheckedChange={(checked) => setVehicleForm(prev => ({ ...prev, is_featured: checked }))}
                data-testid="modal-checkbox-featured"
              />
              <Label htmlFor="is_featured" className="text-[#2C4A3B] cursor-pointer">
                Feature this vehicle on homepage
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVehicleModal(false)}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveVehicle}
              className="btn-primary"
              data-testid="save-vehicle-btn"
            >
              {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
